import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Building2, Home, Navigation } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { toast } from 'sonner';
import { submitContactRequest, submitQuoteRequest } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Demande de devis',
    message: ''
  });
  
  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    phone: '',
    project_type: '',
    location: '',
    budget: '',
    description: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleQuoteChange = (e) => {
    setQuoteData({
      ...quoteData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Soumettre la demande de contact au backend
      const response = await submitContactRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      });
      
      toast.success(response.message || 'Message envoyé avec succès! Nous vous recontacterons rapidement.');
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'Demande de devis',
        message: ''
      });
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingQuote(true);
    
    try {
      // Soumettre la demande de devis au backend
      const response = await submitQuoteRequest(quoteData);
      
      toast.success(response.message || 'Demande de devis envoyée avec succès! Nous vous contacterons rapidement.');
      
      // Réinitialiser le formulaire
      setQuoteData({
        name: '',
        email: '',
        phone: '',
        project_type: '',
        location: '',
        budget: '',
        description: ''
      });
    } catch (error) {
      toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Page Header */}
      <section className="text-white py-20 mb-16" style={{
        background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
      }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}>
            Contactez-<span style={{ 
              background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Nous</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Une question ? Un projet ? N'hésitez pas à nous contacter. Notre équipe est à votre disposition pour vous répondre.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-32">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#0066CC' }}>
                Nos <span style={{ 
                  background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Coordonnées</span>
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center text-brand-gold flex-shrink-0">
                    <Phone size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Téléphone</h3>
                    <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-brand-gold transition-colors">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center text-brand-blue flex-shrink-0">
                    <Mail size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Email</h3>
                    <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-brand-blue transition-colors break-all">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-gold/10 rounded-lg flex items-center justify-center text-brand-gold flex-shrink-0">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Localisation</h3>
                    <p className="text-gray-600">{contactInfo.location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center text-brand-blue flex-shrink-0">
                    <Clock size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Horaires</h3>
                    <p className="text-gray-600">{contactInfo.hours}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-4">Demande Urgente ?</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Pour toute urgence, n'hésitez pas à nous appeler directement.
                </p>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="block w-full bg-brand-gold text-white text-center px-6 py-3 rounded-lg font-semibold hover:bg-brand-gold-dark transition-all hover:shadow-lg"
                >
                  Appelez Maintenant
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#0066CC' }}>
                Demande de <span style={{ 
                  background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Mon Devis Gratuit</span>
              </h2>
              <p className="text-gray-600 mb-8">
                Remplissez ce formulaire et nous vous recontacterons dans les plus brefs délais pour discuter de votre projet.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                      placeholder="04XX XX XX XX"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Type de projet *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
                    >
                      <option value="Demande de devis">Demande de devis</option>
                      <option value="Rénovation complète">Rénovation complète</option>
                      <option value="Cuisine / Salle de bain">Cuisine / Salle de bain</option>
                      <option value="Extension">Extension</option>
                      <option value="Construction neuve">Construction neuve</option>
                      <option value="Toiture / Façade">Toiture / Façade</option>
                      <option value="Aménagement intérieur">Aménagement intérieur</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Décrivez votre projet *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all resize-none"
                    placeholder="Décrivez votre projet en quelques mots : type de travaux, surface, délais souhaités, budget estimé..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-gold text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brand-gold-dark transition-all hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Envoyer ma demande
                    </>
                  )}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  * Champs obligatoires. Vos données sont protégées et ne seront jamais partagées.
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Zone d'intervention */}
        <div className="mt-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12" style={{
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #f9a01b, #ffd230)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}>
            {/* Header principal - Belgique */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-gold rounded-full mb-4">
                <MapPin size={32} className="text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0A2463' }}>
                Nous intervenons <span style={{ 
                  background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>partout en Belgique</span>
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                De Bruxelles à toute la Belgique, nous réalisons vos projets de rénovation et construction avec le même professionnalisme
              </p>
            </div>

            {/* Encadré mis en avant - Toute la Belgique */}
            <div className="mb-10 p-8 rounded-xl" style={{
              background: 'linear-gradient(135deg, #0A2463 0%, #0D2F7E 50%, #1035A0 100%)'
            }}>
              <div className="text-center">
                <h3 className="text-white font-bold text-2xl mb-4">
                  ✨ Projets d'envergure acceptés dans toute la Belgique
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="mb-3 flex justify-center">
                      <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/ij4u81ip_Region-de-Bruxelles-Capitale-1.jpg" 
                          alt="Région de Bruxelles-Capitale"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    <p className="text-white font-semibold mb-1">Région de Bruxelles</p>
                    <p className="text-white/80 text-sm">Notre zone principale</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="mb-3 flex justify-center">
                      <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/e4onlkd8_wallonie_coq_fb.png" 
                          alt="Wallonie - Coq Hardi"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    <p className="text-white font-semibold mb-1">Wallonie</p>
                    <p className="text-white/80 text-sm">Brabant Wallon, Namur, Liège...</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                    <div className="mb-3 flex justify-center">
                      <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 shadow-lg">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_build-transform-6/artifacts/u4sk2rdf_Flemish_coat_of_arms.png" 
                          alt="Flandre - Lion des Flandres"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                    <p className="text-white font-semibold mb-1">Flandre</p>
                    <p className="text-white/80 text-sm">Brabant Flamand, Anvers, Gand...</p>
                  </div>
                </div>
                <p className="text-brand-gold font-semibold text-lg mt-6">
                  📞 Contactez-nous pour discuter de votre projet, où que vous soyez en Belgique !
                </p>
              </div>
            </div>

            {/* Section détaillée - Bruxelles (zone principale) */}
            <div className="bg-gray-50 rounded-xl p-6 md:p-8">
              <h3 className="text-2xl font-bold mb-2 text-center" style={{ color: '#0A2463' }}>
                📍 Notre zone d'intervention <span style={{ 
                  background: 'linear-gradient(to bottom, #f9a01b 0%, #ffd230 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>principale</span>
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Région de Bruxelles-Capitale - Intervention rapide dans les 19 communes
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  'Anderlecht', 'Auderghem', 'Berchem-Sainte-Agathe',
                  'Bruxelles-Ville', 'Etterbeek', 'Evere',
                  'Forest', 'Ganshoren', 'Ixelles',
                  'Jette', 'Koekelberg', 'Molenbeek-Saint-Jean',
                  'Saint-Gilles', 'Saint-Josse-ten-Noode', 'Schaerbeek',
                  'Uccle', 'Watermael-Boitsfort', 'Woluwe-Saint-Lambert',
                  'Woluwe-Saint-Pierre'
                ].map((commune, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-white rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-gold flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span className="text-gray-700 font-medium text-sm">{commune}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
